import React, { memo } from 'react';
import { Segment } from '../models/Segment';
import { TranscriptSelectionManager } from '../managers/TranscriptSelectionManager';
import SegmentHeader from './SegmentHeader';
import TranscriptEditorParagraph from './TranscriptEditorParagraph';
import styled from 'styled-components';
import { defaultTheme } from './theme';
import { TranscriptEditorStore } from '../TranscriptEditorStore';

// Styled component for paragraph container
const ParagraphRowContainer = styled.div`
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.md};
`;

export interface TranscriptSegmentWrapperProps {
  segment: Segment;
  segmentId: string;
  editorStore: TranscriptEditorStore;
  selectionManager: TranscriptSelectionManager | null;
  handleElementClick: (
    e: React.MouseEvent, 
    elementId: string, 
    element: HTMLElement, 
    startTime?: number
  ) => void;
  searchTerm: string;
  visibleParagraphIds: Set<string> | null;
  counter: number;
}

/**
 * Memoized segment wrapper component that prevents re-renders when parent state changes
 * By wrapping each segment in a memoized component, we can prevent re-renders
 * of all paragraphs when only one element changes
 */
const TranscriptSegmentWrapper: React.FC<TranscriptSegmentWrapperProps> = memo(({
  segment,
  segmentId,
  editorStore,
  selectionManager,
  handleElementClick,
  searchTerm,
  visibleParagraphIds
}) => {
  // For debugging
  // console.log('Rendering segment:', segment.id);

  return (
    <React.Fragment>
      <SegmentHeader
        segment={segment}
        editorStore={editorStore}
      />
      
      {segment.paragraphs
        .filter(p => !visibleParagraphIds || visibleParagraphIds.has(p.id))
        .map(paragraph => (
          <ParagraphRowContainer
            key={`para-${paragraph.id}`}
            data-paragraph-id={paragraph.id}
          >
            <TranscriptEditorParagraph
              paragraph={paragraph}
              segmentId={segmentId}
              editorStore={editorStore}
              selectionManager={selectionManager}
              onElementClick={handleElementClick}
              searchTerm={searchTerm}
              counter={paragraph.counter}
            />
          </ParagraphRowContainer>
        ))}
    </React.Fragment>
  );
}, (prevProps, nextProps) => {

  // Custom comparison function that ignores function reference changes
  if (prevProps.counter !== nextProps.counter) return false;
  if (prevProps.segment !== nextProps.segment) return false;
  if (prevProps.segmentId !== nextProps.segmentId) return false;
  if (prevProps.searchTerm !== nextProps.searchTerm) return false;

  if (prevProps.segment.paragraphs !== nextProps.segment.paragraphs) return false;
  if (prevProps.segment.paragraphs.length !== nextProps.segment.paragraphs.length) return false;

  if (prevProps.segment.speakerLabel !== nextProps.segment.speakerLabel) return false;

  if (prevProps.segment.startTime !== nextProps.segment.startTime) return false;
  if (prevProps.segment.endTime !== nextProps.segment.endTime) return false;

  // For visibleParagraphIds, do a more thorough check
  const prevVisible = prevProps.visibleParagraphIds;
  const nextVisible = nextProps.visibleParagraphIds;
  
  if (prevVisible === null && nextVisible === null) {
    // Both null, considered equal
  } else if (!prevVisible || !nextVisible) {
    // One is null, the other isn't
    return false;
  } else if (prevVisible.size !== nextVisible.size) {
    // Different sizes
    return false;
  } else {
    // Check if all IDs in prevVisible are also in nextVisible
    for (const id of prevVisible) {
      if (!nextVisible.has(id)) return false;
    }
  }
  
  // For selectionManager, only care if it exists or not
  if (!!prevProps.selectionManager !== !!nextProps.selectionManager) return false;
  
  // For editorStore, check identity
  if (prevProps.editorStore !== nextProps.editorStore) return false;
  
  // We ignore handleElementClick reference changes because it's just a callback
  
  // If we made it here, props are considered equal
  return true;
});

TranscriptSegmentWrapper.displayName = 'TranscriptSegmentWrapper';

export default TranscriptSegmentWrapper;